import { ref } from "vue";
import { RGSClient } from "stake-engine";
import type {
  AuthenticateConfig,
  Balance,
  JurisdictionFlags,
  Round,
} from "stake-engine";

type RgsClient = ReturnType<typeof RGSClient>;

// Stake Engine launches games with sessionID/rgs_url (and lang, currency,
// device, social, demo) in the URL — see /docs/reference/url-structure.
// Without session parameters we fall back to the local simulation.
const urlParams = new URLSearchParams(window.location.search);
const isRgsMode = urlParams.has("sessionID") && urlParams.has("rgs_url");

// Social casinos launch the game with social=true. In that mode,
// restricted gambling wording (bet, wager, cash, ...) must be replaced
// with social-safe alternatives (play, coins, ...).
const isSocialMode = ["true", "1"].includes(
  (urlParams.get("social") ?? "").toLowerCase()
);

export function useRgs() {
  const ready = ref(false);
  const initError = ref<string | null>(null);
  const balance = ref<Balance | null>(null);
  const config = ref<AuthenticateConfig | null>(null);
  const jurisdictionFlags = ref<JurisdictionFlags | null>(null);
  const roundActive = ref(false);

  let client: RgsClient | null = null;

  const onBalanceUpdate = (e: Event) => {
    balance.value = (e as CustomEvent<Balance>).detail;
  };

  const onRoundActive = (e: Event) => {
    roundActive.value = (e as CustomEvent<{ active: boolean }>).detail.active;
  };

  async function init(): Promise<void> {
    if (!isRgsMode || client) return;
    window.addEventListener("balanceUpdate", onBalanceUpdate);
    window.addEventListener("roundActive", onRoundActive);
    try {
      // The RGS is always https in production; allow http for a local mock.
      const rgsUrl = urlParams.get("rgs_url") ?? "";
      const isLocal = /^(localhost|127\.0\.0\.1)([:/]|$)/.test(rgsUrl);
      client = RGSClient({
        url: window.location.href,
        protocol: isLocal ? "http" : "https",
      });
      const auth = await client.Authenticate();
      config.value = auth.config;
      jurisdictionFlags.value = auth.jurisdictionFlags;
      balance.value = auth.balance;
      // Settle any round left unfinished by a previous session.
      if (auth.round?.active) {
        await client.EndRound();
      }
      ready.value = true;
    } catch (e) {
      console.error("RGS authentication failed", e);
      initError.value = toMessage(e, "Failed to connect to Stake Engine");
    }
  }

  function dispose(): void {
    window.removeEventListener("balanceUpdate", onBalanceUpdate);
    window.removeEventListener("roundActive", onRoundActive);
  }

  async function play(amount: number, mode: string): Promise<Round> {
    if (!client) throw new Error("RGS client is not initialised");
    try {
      const response = await client.Play({ amount, mode });
      return response.round;
    } catch (e) {
      throw new Error(toMessage(e, "Rejected by the server"));
    }
  }

  async function endRound(): Promise<void> {
    if (!client) throw new Error("RGS client is not initialised");
    await client.EndRound();
  }

  return {
    isRgsMode,
    isSocialMode,
    ready,
    initError,
    balance,
    config,
    jurisdictionFlags,
    roundActive,
    init,
    dispose,
    play,
    endRound,
  };
}

// The RGS client throws Error(data) with a response object, which
// stringifies to "[object Object]" — swap that for something readable.
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message && e.message !== "[object Object]") {
    return e.message;
  }
  return fallback;
}
